import { useEffect, useState } from "react";
import { Banner, Button, EmptyState, Icon, Mark, Select, Spinner } from "@vavr/ui";
import { api } from "../shared/api.ts";
import { QrCode } from "../shared/qr.tsx";
import { day } from "../shared/format.ts";
import { useGuardian } from "./context.tsx";
import { msToNextWindow, passFor, qrText, secondsLeft, type CachedPass } from "./pass.ts";

export function PickupCodeView({ siteName, qr, left, notAfter, tz, offline }: { siteName: string; qr: string; left: number; notAfter: number; tz: string; offline: boolean }) {
  return (
    <section className="pmu-g-pass" aria-labelledby="pmu-g-pass-title">
      <header className="pmu-g-pass__head">
        <Mark size={36} label="" />
        <div>
          <p className="pmu-g-pass__eyebrow">Pickup pass</p>
          <h2 id="pmu-g-pass-title" className="pmu-g-pass__site">{siteName}</h2>
        </div>
        {offline && <span className="pmu-g-pass__offline"><Icon name="cloud-off" />Offline</span>}
      </header>
      <div className="pmu-g-pass__body">
        <p className="pmu-g-pass__lead">Show this at the door</p>
        <div className="pmu-g-qr">
          <QrCode text={qr} size={264} ecc="L" label="Your pickup code. Hold it up to the kiosk camera." />
        </div>
        <div className="pmu-g-pass__meter" aria-hidden="true"><span style={{ width: `${(left / 30) * 100}%` }} /></div>
        <p className="pmu-g-qr__timer" aria-live="off">New code in <strong>{left}</strong> s</p>
        <p className="pmu-small pmu-muted pmu-g-center">It changes every 30 seconds, so a screenshot will not work. {offline ? "Works without signal." : ""}</p>
      </div>
      <footer className="pmu-g-pass__foot pmu-small">This phone's pass lasts until {day(notAfter * 1000, tz)}.</footer>
    </section>
  );
}

/** The rotating signed QR (plan §7). Works offline once the pass is cached on the phone. */
export function PickupCode() {
  const { overview, kv } = useGuardian();
  const sites = overview.sites.filter((s) => s.children.length);
  const [siteId, setSiteId] = useState(sites[0]?.site.id ?? "");
  const s = sites.find((x) => x.site.id === siteId);
  const [pass, setPass] = useState<CachedPass | null>(null);
  const [state, setState] = useState<{ loading: boolean; error: string | null; offline: boolean }>({ loading: true, error: null, offline: false });
  const [qr, setQr] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [force, setForce] = useState(0);

  useEffect(() => {
    if (!siteId) return;
    let live = true;
    setState({ loading: true, error: null, offline: false });
    passFor(kv, siteId, (site, holder) => api("POST", `/api/v1/guardian/sites/${site}/pass`, { holder_key: holder }), Date.now(), force > 0)
      .then(({ cached, refreshError }) => {
        if (!live) return;
        setPass(cached);
        setState({ loading: false, offline: !!refreshError, error: cached ? null : "Connect to the internet once to set up your pickup code on this phone." });
      });
    return () => { live = false; };
  }, [kv, siteId, force]);

  // Re-sign at each 30 s window; tick every second for the countdown.
  useEffect(() => {
    if (!pass) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout>;
    const render = () => { qrText(kv, pass).then((t) => live && setQr(t)); timer = setTimeout(render, msToNextWindow(Date.now()) + 20); };
    render();
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => { live = false; clearTimeout(timer); clearInterval(tick); };
  }, [kv, pass]);

  if (!s) return <EmptyState icon="qr" title="No children linked yet" description="Your pickup code appears once a school links you to a child." />;
  return (
    <div className="pmu-stack pmu-g-code-page">
      {sites.length > 1 && <Select label="School or center" value={siteId} onValueChange={(v) => setSiteId(v ?? "")} options={sites.map((x) => ({ value: x.site.id, label: x.site.name }))} />}
      {state.loading && !pass ? <Spinner label="Preparing your code" /> : state.error ? <Banner tone="warning" title={state.error} actions={<Button size="sm" onClick={() => setForce((n) => n + 1)}>Try again</Button>} /> : pass && qr ? (
        <PickupCodeView siteName={s.site.name} qr={qr} left={secondsLeft(now)} notAfter={pass.not_after} tz={s.site.tz} offline={state.offline} />
      ) : <Spinner label="Preparing your code" />}
      <p className="pmu-small pmu-muted pmu-g-footnote"><Icon name="shield" /> Signed by {s.site.name} and by this phone. Staff still check who you are at the door.</p>
    </div>
  );
}
